import {
  faCalendarDays,
  faChartLine,
  faCog,
  faFileExport,
  faFileInvoice,
  faHome,
  faMagnifyingGlass,
  faMessageSms,
  faPaperPlaneTop,
  faThList,
  faUser,
  faUserTie
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { TextInput } from '~/components/Form'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { useConfig } from '~/domains/configuration/queries/config'
import { useTaxonomyList } from '~/domains/taxonomies/queries/useTaxonomyList'
import {
  searchRow,
  sidebar,
  sidebarSection,
  sidebarSectionTitle,
  sidebarWrapper
} from '~/pages/styles/settings/Settings.css'
import { SidebarSwitchContext } from '~/routes/_dashboard/-components/sidebar'
import { themeClass } from '~/styles/theme.css'
import { AuthContext } from '~/utils/contextes'
import { matchesSearch } from '~/utils/searchMatch'
import type { SettingsMenuItemData } from './SettingsMenuItem'
import { SettingsMenuItem } from './SettingsMenuItem'

type MenuSection = {
  title: string
  icon: typeof faUser
  items: SettingsMenuItemData[]
}

export const SettingsMenu = ({ currentPath }: { currentPath?: string }) => {
  const currentUser = useContext(AuthContext)
  const { setShowDashboardMenu } = useContext(SidebarSwitchContext)
  const { data: isAccountantEnabled } = useConfig('feature_flags.accountant')
  const { data: isTalentplugVisible } = useConfig('talentplug.active')
  const { data: isFactorEnabled } = useConfig('factor.enabled')
  const { data: isAdvertsdataEnabled } = useConfig('advertsdata.enabled')
  const { data: isArmadoEnabled } = useConfig('armado.enabled')
  const { data: isTempoEnabled } = useConfig('tempo.enabled')
  const { data: invoiceEnabled } = useConfig('finance.invoice_enabled')
  const { data: DPOUserId } = useConfig('gdpr.dpo_user')
  const { data: legacyUrl = null } = useConfig('organization.legacy_url')
  const isDPOUser = DPOUserId === currentUser?.id
  const menuRef = useRef<HTMLDivElement>(null)
  const { data: taxonomyData, isLoading: isTaxonomyLoading } = useTaxonomyList()
  const [search, setSearch] = useState<string>('')

  const isAdmin = window.JobConfig
    ? window.JobConfig.user?.availableGroups.some((group) => group.id === 1)
    : currentUser?.groups.some((group) => group.id === 1)

  const isSuperAdmin = window.JobConfig
    ? window.JobConfig.user?.superuser
    : currentUser?.isSuperUser

  const editableTaxonomies = useMemo(() => {
    if (!taxonomyData?.items) return []
    return taxonomyData.items
      .filter((taxonomy) => taxonomy.isEditable && taxonomy.slug)
      .sort((a, b) => a.localizedName.localeCompare(b.localizedName))
  }, [taxonomyData])

  const referentielsItems: SettingsMenuItemData[] = useMemo(() => {
    const items: SettingsMenuItemData[] = [
      {
        label: 'Gestion des tags',
        tags: ['étiquettes'],
        href: '/admin/securite/tags',
        requiresAdmin: true
      }
    ]

    editableTaxonomies.forEach((taxonomy) => {
      if (taxonomy.slug) {
        items.push({
          label: taxonomy.localizedName,
          tags: ['référentiel'],
          href: `/admin/${taxonomy.slug}/`,
          requiresAdmin: true
        })
      }
    })

    return items
  }, [editableTaxonomies])

  const menuSections: MenuSection[] = [
    {
      title: 'Compte',
      icon: faUser,
      items: [
        {
          label: 'Mon profil',
          tags: ['compte', 'mot de passe'],
          to: '/settings/'
        },
        {
          label: 'Agenda',
          tags: ['calendrier'],
          to: '/settings/calendar'
        }
      ]
    },
    {
      title: 'Général',
      icon: faCog,
      items: [
        {
          label: 'Informations générales',
          tags: ['société'],
          href: '/admin/securite/general',
          requiresAdmin: true
        },
        {
          label: 'Paramétrage général',
          tags: ['configuration'],
          href: '/admin/securite/parameters',
          requiresAdmin: true
        },
        {
          label: 'Gestion des agences',
          href: '/admin/agences/index/0',
          requiresAdmin: true
        },
        {
          label: 'Gestion des utilisateurs',
          tags: ['comptes'],
          href: '/admin/securite/users_groups',
          requiresAdmin: true
        },
        {
          label: 'Connexions des utilisateurs',
          tags: ['login', 'sessions'],
          href: '/admin/users/logins',
          requiresAdmin: true
        },
        {
          label: 'Gestion du FTP',
          href: '/admin/ftps',
          requiresAdmin: true
        },
        {
          label: 'Filtrage par IP',
          href: '/admin/securite/ip',
          requiresAdmin: true
        },
        {
          label: 'Champs obligatoires',
          tags: ['informations requises'],
          to: '/settings/mandatory-fields',
          requiresAdmin: true
        },
        {
          label: 'Couleur des actions',
          tags: ['couleurs'],
          href: '/admin/securite/colors_actions'
        },
        {
          label: 'Pages de recherche',
          tags: ['listings'],
          href: '/admin/users/listings'
        },
        {
          label: 'Gestion RGPD',
          tags: ['dpo', 'anonymisation'],
          to: '/settings/gdpr',
          requiresDPO: true
        },
        {
          label: "Santé de l'instance",
          tags: ['health'],
          href: '/admin/securite/health',
          requiresAdmin: true
        },
        {
          label: 'Utilisateurs applicatifs',
          tags: ['api', 'webhooks'],
          href: '/admin/users/apps',
          requiresAdmin: true
        },
        {
          label: 'Superadmin',
          href: '/admin/securite/superadmin',
          requiresSuperAdmin: true
        },
        {
          label: 'Tests Superadmin',
          href: '/admin/securite/superadmin_test',
          requiresSuperAdmin: true
        },
        {
          label: 'Fonctionnalités',
          tags: ['feature flags'],
          href: '/admin/securite/feature_flags',
          requiresSuperAdmin: true
        },
        {
          label: 'Poids du scoring matching',
          tags: ['matching'],
          to: '/settings/matching-score-config',
          requiresSuperAdmin: true
        },
        {
          label: 'Extensions',
          tags: ['liaisons', 'intégrations'],
          to: '/settings/extensions',
          requiresSuperAdmin: true
        }
      ]
    },
    {
      title: 'Liaison gestion',
      icon: faFileInvoice,
      items: [
        {
          label: 'Configuration Comptable',
          tags: ['sage'],
          href: '/admin/entreprises/config_comptable',
          requiresAdmin: true,
          requiresAccountant: true
        },
        {
          label: 'Import comptable (candidats)',
          href: '/admin/candidats/fichier_comptable',
          requiresAdmin: true,
          requiresAccountant: true
        },
        {
          label: 'Import comptable (entreprises)',
          href: '/admin/entreprises/fichier_comptable',
          requiresAdmin: true,
          requiresAccountant: true
        },
        {
          label: 'Reporting comptable (candidats)',
          href: '/admin/candidats/fichier_comptable_candidats_logs',
          requiresAdmin: true,
          requiresAccountant: true
        },
        {
          label: 'Reporting comptable (entreprises)',
          href: '/admin/entreprises/fichier_comptable_logs',
          requiresAdmin: true,
          requiresAccountant: true
        },
        {
          label: 'Contrats',
          href: '/admin/securite/contract',
          requiresAdmin: true
        }
      ]
    },
    {
      title: 'Agenda',
      icon: faCalendarDays,
      items: [
        {
          label: "Couleurs d'agenda",
          href: '/admin/users/colors'
        }
      ]
    },
    {
      title: 'Email',
      icon: faPaperPlaneTop,
      items: [
        {
          label: 'Désabonnement e-mails',
          tags: ['unsubscribe'],
          href: '/admin/unsubscribe/set_link',
          requiresAdmin: true
        },
        {
          label: 'Gérer les NPAI',
          tags: ['bounce'],
          href: '/admin/candidats/import_archive',
          requiresAdmin: true
        },
        {
          label: 'Signatures',
          href: '/admin/signatures',
          requiresAdmin: true
        },
        {
          label: "Modèles d'e-mail",
          tags: ['templates'],
          href: '/admin/templates'
        },
        {
          label: 'Séquences de mails',
          tags: ['mailing'],
          href: '/admin/securite/mailing_sequences',
          requiresSuperAdmin: true
        }
      ]
    },
    {
      title: 'SMS',
      icon: faMessageSms,
      items: [
        {
          label: 'Configuration SMS',
          href: '/admin/securite/sms',
          requiresAdmin: true
        },
        {
          label: 'Consommation SMS',
          to: '/settings/sms',
          requiresAdmin: true
        },
        {
          label: 'Modèles de SMS',
          to: '/settings/sms-templates'
        }
      ]
    },
    {
      title: 'Offres — Commandes',
      icon: faUserTie,
      items: [
        {
          label: 'Configuration des commandes',
          to: '/settings/orders',
          requiresAdmin: true
        },
        {
          label: 'Espace TalentPlug',
          tags: ['jobboard', 'diffusion'],
          href: '/admin/jobdistrib_accounts',
          requiresAdmin: true,
          requiresTalentplug: true
        },
        {
          label: 'Comptes TalentPlug',
          tags: ['jobboard'],
          href: '/admin/jobdistrib_accounts/user',
          requiresTalentplug: true
        },
        {
          label: 'Candidatures',
          to: '/settings/applications',
          requiresAdmin: true
        },
        {
          label: 'Configuration des offres',
          to: '/settings/offers',
          requiresAdmin: true
        }
      ]
    },
    {
      title: 'Candidats',
      icon: faUser,
      items: [
        {
          label: 'Disponibilité - Délais',
          to: '/settings/applicant-availability'
        },
        {
          label: 'Import de CV par Email',
          tags: ['jobmail'],
          to: '/settings/resumes-inbound',
          requiresAdmin: true
        },
        {
          label: 'Prises de référence',
          to: '/settings/applicant-experience',
          requiresAdmin: true
        },
        {
          label: 'Gabarits de CV',
          tags: ['cv'],
          to: '/settings/resume-templates',
          requiresAdmin: true
        }
      ]
    },
    {
      title: 'Entreprises',
      icon: faChartLine,
      items: [
        {
          label: 'Modèles de documents',
          to: '/settings/document-templates/',
          requiresAdmin: true
        },
        {
          label: 'Factures',
          tags: ['facturation'],
          to: '/settings/invoice',
          requiresAdmin: true,
          requiresInvoiceEnabled: true
        },
        {
          label: 'Bon de commande',
          tags: ['bdc'],
          href: '/admin/contracts/export_bdc_config',
          requiresSuperAdmin: true
        },
        {
          label: 'Entreprises',
          href: '/admin/securite/company',
          requiresAdmin: true
        },
        {
          label: 'Préférences factor',
          tags: ['affacturage'],
          href: '/admin/factor/config',
          requiresAdmin: true,
          requiresFactor: true
        },
        {
          label: 'Demandes au factor',
          href: '/admin/factor',
          requiresFactor: true
        },
        {
          label: 'Détection de leads - Gestion des identifiants',
          tags: ['advertsdata', 'leads'],
          href: '/admin/securite/advertsdata',
          requiresAdmin: true,
          requiresAdvertsdata: true
        },
        {
          label: 'Armado',
          href: '/admin/securite/armado',
          requiresAdmin: true,
          requiresArmado: true
        },
        {
          label: 'Tempo',
          to: '/settings/tempo',
          requiresAdmin: true,
          requiresTempo: true
        }
      ]
    },
    {
      title: 'Importer / exporter',
      icon: faFileExport,
      items: [
        {
          label: 'Importer des entreprises',
          href: '/admin/Importxlsentreprise/index',
          requiresAdmin: true
        },
        {
          label: 'Exporter des entreprises',
          href: '/admin/entreprises/export',
          requiresAdmin: true
        },
        {
          label: 'Exporter les données',
          href: '/admin/securite/exports',
          requiresSuperAdmin: true
        }
      ]
    },
    {
      title: 'Reporting',
      icon: faChartLine,
      items: [
        {
          label: 'Envoi des statistiques',
          href: '/admin/statistiques/config',
          requiresAdmin: true
        },
        {
          label: 'Visibilité du reporting',
          href: '/admin/statistiques/users',
          requiresAdmin: true
        },
        {
          label: "Suivi de l'activité",
          href: '/admin/statistiques/pilotage'
        },
        {
          label: "Suivi de l'activité commerciale",
          href: '/admin/statistiques/activite'
        },
        {
          label: 'Suivi des recrutements',
          href: '/admin/statistiques/recrutement'
        },
        {
          label: 'Historique des pushs offres',
          href: '/admin/push/rechercher_offres'
        },
        {
          label: 'Historique des placements actifs',
          href: '/admin/push/rechercher'
        },
        {
          label: "Chiffre d'affaire et prévisionnel",
          href: '/admin/dashboard/stats'
        }
      ]
    },
    {
      title: 'Référentiels',
      icon: faThList,
      items: referentielsItems
    }
  ]

  // keep a ref to the current active menu element
  const activeRef = useRef<HTMLAnchorElement | HTMLSpanElement | null>(null)

  useEffect(() => {
    if (activeRef.current && menuRef.current && !isTaxonomyLoading) {
      const active = activeRef.current as HTMLElement
      const menu = menuRef.current
      const activeTop = active.offsetTop
      const menuScrollTop = menu.scrollTop
      const menuHeight = menu.clientHeight
      const activeHeight = active.clientHeight
      if (
        activeTop < menuScrollTop ||
        activeTop + activeHeight > menuScrollTop + menuHeight
      ) {
        menu.scrollTop = activeTop - menuHeight / 2 + activeHeight / 2 - 60
      }
    }
  }, [isTaxonomyLoading])

  if (isTaxonomyLoading) {
    return (
      <div className={clsx(sidebarWrapper, themeClass)}>
        <nav className={sidebar} ref={menuRef}>
          <Loader small />
        </nav>
      </div>
    )
  }

  return (
    <div className={clsx(sidebarWrapper, themeClass)}>
      <nav className={sidebar} ref={menuRef}>
        <div className={searchRow}>
          <Button
            onClick={() => setShowDashboardMenu(true)}
            title="Voir le menu principal"
            aria-label="Voir le menu principal"
            buttonSize="small"
            variation={ButtonVariation.Neutral}
          >
            <FontAwesomeIcon icon={faHome} />
          </Button>
          <TextInput
            standalone
            placeholder="Rechercher"
            type="search"
            name="search"
            value={search}
            leadingIcon={faMagnifyingGlass}
            small
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
            wrapper={false}
          />
        </div>
        {menuSections.map((section) => {
          const visibleItems = section.items.filter((item) => {
            if (item.requiresAdmin && !isAdmin) return false
            if (item.requiresAccountant && !isAccountantEnabled) return false
            if (item.requiresSuperAdmin && !isSuperAdmin) return false
            if (item.requiresTalentplug && !isTalentplugVisible) return false
            if (item.requiresFactor && !isFactorEnabled) return false
            if (item.requiresAdvertsdata && !isAdvertsdataEnabled) return false
            if (item.requiresArmado && !isArmadoEnabled) return false
            if (item.requiresTempo && !isTempoEnabled) return false
            if (item.requiresInvoiceEnabled && !invoiceEnabled) return false
            if (item.requiresDPO && !isDPOUser && !isSuperAdmin) return false
            if (
              search &&
              !matchesSearch(
                search,
                section.title,
                item.label,
                ...(item.tags ?? [])
              )
            )
              return false
            return true
          })

          if (visibleItems.length === 0) return null

          return (
            <div key={section.title} className={sidebarSection}>
              <div className={sidebarSectionTitle}>
                <FontAwesomeIcon icon={section.icon} />
                <span>{section.title}</span>
              </div>
              {visibleItems.map((item) => (
                <SettingsMenuItem
                  key={item.label}
                  item={item}
                  isActive={
                    item.to === currentPath || item.href === currentPath
                  }
                  legacyUrl={legacyUrl ?? null}
                  activeRef={activeRef}
                />
              ))}
            </div>
          )
        })}
      </nav>
    </div>
  )
}
